import i18n, { ResourceKey } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend, { HttpBackendOptions } from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

void i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init<HttpBackendOptions>({
    supportedLngs: ['en-US'],
    ns: ['translation', 'ui', 'integrations', 'trust'],
    fallbackLng: {
      default: ['en-US'],
    },
    debug: false,
    returnNull: false,
    backend: {
      loadPath: (languages, namespaces) => `${languages[0]}/${namespaces[0]}`,
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async request(options, url, payload, callback) {
        const [lng, ns] = url.split('/');
        const data = (await import(`./locales/${lng}/${ns}.json`)) as { default: ResourceKey };
        callback(null, { status: 200, data: data.default });
      },
    },
  });

// Dev only: reload edited locale JSON in place instead of a full page reload.
//
// The locale files load via the dynamic import above, and vite treats a
// dynamic import as a hard-reload boundary by default. `import.meta.hot` and
// `accept()` MUST live in THIS module (the one that imports the locales) for it
// to be the accepting boundary — moving them to another module breaks the
// boundary and vite falls back to a full reload. So the handler stays here; we
// only keep it minimal and never re-run init() (an edit to this file triggers a
// normal reload, which re-inits cleanly on a fresh page).
if (import.meta.hot) {
  import.meta.hot.on(
    'vite:beforeUpdate',
    (payload: { updates?: Array<{ path?: string; acceptedPath?: string }> }) => {
      const touchedLocale = (payload.updates ?? []).some((u) =>
        /locales\/.*\.json$/.test(u.path ?? u.acceptedPath ?? ''),
      );
      if (!touchedLocale) return;
      void i18n.reloadResources().then(() => {
        // Let runtime overlays (e.g. org terminology overrides applied with
        // addResource) re-apply before we re-render; reloadResources overwrites
        // the namespace and would otherwise drop them until the next sync.
        i18n.emit('hmr:localesReloaded');
        // Re-emit languageChanged so every useTranslation re-runs t() with the
        // fresh values — no navigation, no reload, no lost place.
        i18n.emit('languageChanged', i18n.language);
      });
    },
  );
  // Accept the locale JSON modules so vite performs a hot update rather than a
  // full reload; the actual re-read happens in the handler above.
  import.meta.hot.accept();
}

export default i18n;
